import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAllProducts, useCanManageProducts } from '@/hooks/useVisibleProducts'
import { setProducts, ProductDef } from '@/config/products'
import { platformSettingsService, ProductInput } from '@/services/platformSettingsService'
import { HttpError } from '@/lib/httpError'
import ProductIcon from '@/components/ProductIcon'

type Notice = { kind: 'ok' | 'error'; text: string } | null

interface FormState {
  id?: string
  builtin: boolean
  name: string
  description: string
  icon: string
  href: string
  external: boolean
  enabled: boolean
}

const ICON_SIZE = 128
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024

/**
 * Turn a picked image into a 128x128 PNG data URL. Scaling here keeps the
 * stored icon small whatever was picked, and re-encoding through a canvas
 * means only plain pixels reach the service.
 */
function imageToIcon(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('read'))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('decode'))
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = ICON_SIZE
        canvas.height = ICON_SIZE
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('canvas'))
        // Fit inside the square, keep proportions, transparent margins
        const scale = Math.min(ICON_SIZE / img.width, ICON_SIZE / img.height)
        const w = img.width * scale
        const h = img.height * scale
        ctx.drawImage(img, (ICON_SIZE - w) / 2, (ICON_SIZE - h) / 2, w, h)
        resolve(canvas.toDataURL('image/png'))
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  })
}

const emptyForm: FormState = { builtin: false, name: '', description: '', icon: '', href: '', external: true, enabled: true }

/**
 * The products the console shows: the installation's superuser account adds,
 * changes, hides, reorders and removes them here. Everyone else, other
 * platform owners included, sees the list read-only; the service refuses
 * their changes anyway.
 */
export default function ProductSettingsPage() {
  const { t, i18n } = useTranslation('settings')
  const isOwner = useCanManageProducts()
  const products = useAllProducts()

  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<Notice>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [formError, setFormError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<ProductDef | null>(null)

  const failText = (e: unknown, fallbackKey: string) => {
    if (e instanceof HttpError) {
      if (e.status === 403) return t('products.forbidden')
      if (!e.message.startsWith('errors.')) return e.message // the service's own reason
    }
    return t(fallbackKey)
  }

  const refresh = async () => {
    const r = await platformSettingsService.listProducts()
    setProducts(r.products, r.can_manage)
  }

  const run = async (work: () => Promise<void>, okKey: string, failKey: string) => {
    setBusy(true)
    setNotice(null)
    try {
      await work()
      await refresh()
      setNotice({ kind: 'ok', text: t(okKey) })
    } catch (e) {
      setNotice({ kind: 'error', text: failText(e, failKey) })
    } finally {
      setBusy(false)
    }
  }

  const toggle = (p: ProductDef) =>
    run(async () => { await platformSettingsService.updateProduct(p.id, { enabled: !p.enabled }) }, p.enabled ? 'products.hidden' : 'products.shown', 'products.saveFailed')

  const move = (p: ProductDef, dir: -1 | 1) => {
    const ids = products.map((x) => x.id)
    const i = ids.indexOf(p.id)
    const j = i + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    return run(async () => { await platformSettingsService.reorderProducts(ids) }, 'products.reordered', 'products.saveFailed')
  }

  const remove = (p: ProductDef) =>
    run(async () => { await platformSettingsService.deleteProduct(p.id) }, 'products.removed', 'products.deleteFailed').then(() => setConfirmDelete(null))

  const openAdd = () => { setForm({ ...emptyForm }); setFormError('') }
  const openEdit = (p: ProductDef) => {
    setForm({ id: p.id, builtin: p.builtin, name: p.name, description: p.description, icon: p.iconPath, href: p.builtin ? p.href(i18n.language) : p.href(i18n.language), external: p.external, enabled: p.enabled })
    setFormError('')
  }

  const pickIcon = async (file: File | undefined) => {
    if (!file || !form) return
    if (!/^image\/(png|jpeg|webp|gif|svg\+xml)$/.test(file.type)) { setFormError(t('products.form.uploadType')); return }
    if (file.size > MAX_UPLOAD_BYTES) { setFormError(t('products.form.uploadTooLarge')); return }
    try {
      const icon = await imageToIcon(file)
      setForm({ ...form, icon })
      setFormError('')
    } catch {
      setFormError(t('products.form.uploadFailed'))
    }
  }

  const submit = async () => {
    if (!form) return
    const name = form.name.trim()
    const href = form.href.trim()
    if (!name) { setFormError(t('products.form.nameRequired')); return }
    if (!form.builtin && !/^(https?:\/\/|\/(?!\/))/.test(href)) { setFormError(t('products.form.linkInvalid')); return }
    const icon = form.icon.trim()
    if (icon && !icon.startsWith('data:image/') && !/^(https?:\/\/|\/(?!\/))/.test(icon)) { setFormError(t('products.form.iconInvalid')); return }
    const input: ProductInput = { name, description: form.description.trim(), icon, enabled: form.enabled }
    if (!form.builtin) { input.href = href; input.external = form.external }
    const id = form.id
    await run(async () => {
      if (id) await platformSettingsService.updateProduct(id, input)
      else await platformSettingsService.createProduct(input)
    }, id ? 'products.saved' : 'products.added', 'products.saveFailed')
    setForm(null)
  }

  const field = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm'

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('products.title')}</h1>
          <p className="text-gray-600 mt-2">{t('products.description')}</p>
        </div>
        {isOwner && (
          <button type="button" onClick={openAdd} disabled={busy} className="shrink-0 px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors">
            {t('products.add')}
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <p className="text-sm text-gray-600 mb-4">{t('products.hint')}</p>
        {!isOwner && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">{t('products.ownerOnly')}</div>
        )}

        <ul className="divide-y divide-gray-100">
          {products.map((p, index) => (
            <li key={p.id} className="flex items-center gap-4 py-3">
              {/* A hidden product's details are dimmed; its buttons stay fully usable */}
              <ProductIcon name={p.name} src={p.iconPath} className={`w-10 h-10 rounded-lg shrink-0 ${p.enabled ? '' : 'opacity-60'}`} />
              <div className={`flex-1 min-w-0 ${p.enabled ? '' : 'opacity-60'}`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">{p.name}</span>
                  {p.builtin && <span className="px-2 py-0.5 text-[11px] rounded-full bg-gray-100 text-gray-600">{t('products.builtin')}</span>}
                  <span className={`px-2 py-0.5 text-[11px] rounded-full ${p.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                    {p.enabled ? t('products.state.shown') : t('products.state.hidden')}
                  </span>
                </div>
                {p.description && <div className="text-sm text-gray-600 truncate">{p.description}</div>}
                <div className="text-xs text-gray-400 truncate">{p.href(i18n.language)}{p.external ? ` · ${t('products.form.external')}` : ''}</div>
              </div>
              {isOwner && (
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" title={t('products.actions.up')} aria-label={`${t('products.actions.up')} ${p.name}`} disabled={busy || index === 0} onClick={() => move(p, -1)} className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-30 text-gray-600">↑</button>
                  <button type="button" title={t('products.actions.down')} aria-label={`${t('products.actions.down')} ${p.name}`} disabled={busy || index === products.length - 1} onClick={() => move(p, 1)} className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-30 text-gray-600">↓</button>
                  <button type="button" disabled={busy} onClick={() => toggle(p)} className="px-3 py-1.5 rounded-md text-sm border border-gray-200 hover:bg-gray-50 text-gray-700">
                    {p.enabled ? t('products.actions.hide') : t('products.actions.show')}
                  </button>
                  <button type="button" disabled={busy} onClick={() => openEdit(p)} className="px-3 py-1.5 rounded-md text-sm border border-gray-200 hover:bg-gray-50 text-gray-700">
                    {t('products.actions.edit')}
                  </button>
                  <button type="button" disabled={busy} onClick={() => setConfirmDelete(p)} className="px-3 py-1.5 rounded-md text-sm border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-30">
                    {t('products.actions.delete')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {notice && (
          <div role="status" className={`mt-4 p-3 rounded-lg text-sm border ${notice.kind === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {notice.text}
          </div>
        )}
      </div>

      {form && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="product-form-title">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 id="product-form-title" className="text-lg font-semibold text-gray-900">{form.id ? t('products.form.editTitle') : t('products.form.addTitle')}</h2>
              {form.builtin && <p className="text-xs text-gray-500 mt-1">{t('products.form.builtinNote')}</p>}
            </div>
            <div className="px-6 py-4 space-y-4">
              <label className="block text-sm">
                <span className="text-gray-700">{t('products.form.name')}</span>
                <input className={field} value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">{t('products.form.description')}</span>
                <input className={field} value={form.description} maxLength={200} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">{t('products.form.link')}</span>
                <input className={field} value={form.href} disabled={form.builtin} placeholder="https://" onChange={(e) => setForm({ ...form, href: e.target.value })} />
              </label>
              <div className="block text-sm">
                <span className="text-gray-700">{t('products.form.icon')}</span>
                <div className="mt-1 flex items-center gap-3">
                  <ProductIcon name={form.name || '?'} src={form.icon} className="w-12 h-12 rounded-lg shrink-0 border border-gray-200 object-contain" />
                  <label className="px-3 py-1.5 rounded-md text-sm border border-gray-200 hover:bg-gray-50 text-gray-700 cursor-pointer">
                    {t('products.form.upload')}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="sr-only" aria-label={t('products.form.upload')} onChange={(e) => { void pickIcon(e.target.files?.[0]); e.target.value = '' }} />
                  </label>
                  {form.icon && (
                    <button type="button" onClick={() => setForm({ ...form, icon: '' })} className="px-3 py-1.5 rounded-md text-sm border border-gray-200 hover:bg-gray-50 text-gray-700">
                      {t('products.form.removeIcon')}
                    </button>
                  )}
                </div>
                {!form.icon.startsWith('data:') && (
                  <input className={`${field} mt-2`} value={form.icon} placeholder={t('products.form.iconUrlPlaceholder')} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
                )}
                <span className="text-xs text-gray-500">{t('products.form.iconHint')}</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="accent-primary" checked={form.external} disabled={form.builtin} onChange={(e) => setForm({ ...form, external: e.target.checked })} />
                  {t('products.form.external')}
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="accent-primary" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                  {t('products.form.enabled')}
                </label>
              </div>
              {formError && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{formError}</div>}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">{t('products.form.cancel')}</button>
              <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-600 disabled:opacity-50">
                {busy ? t('products.saving') : t('products.form.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900">{t('products.confirmDelete.title')}</h2>
            <p className="text-sm text-gray-600 mt-2">{t('products.confirmDelete.text', { name: confirmDelete.name })}</p>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDelete(null)} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">{t('products.form.cancel')}</button>
              <button type="button" onClick={() => remove(confirmDelete)} disabled={busy} className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50">{t('products.actions.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
